import { logger } from "@/services/logger";

const log = logger.scope("embeddings");
const REQUEST_TIMEOUT_MS = 30_000;

interface OllamaEmbeddingsResponse {
  embedding?: number[];
}

/**
 * Thin wrapper over Ollama's `/api/embeddings`, mapping its raw shape into a
 * plain `Float32Array` — the same shape-mapping discipline as
 * `services/ai/providers/ollama/ollamaClient.ts`, so nothing above this file
 * knows Ollama's JSON. Requires an embedding-capable model to be pulled
 * (e.g. `ollama pull nomic-embed-text`); the caller decides which via the
 * `embeddingModelId` setting.
 */
export async function embed(baseUrl: string, model: string, text: string): Promise<Float32Array> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(`${baseUrl}/api/embeddings`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ model, prompt: text }),
      signal: controller.signal,
    });
  } catch (err) {
    log.error("Embedding request failed to reach Ollama", err);
    throw new Error("Couldn't reach Ollama for embeddings. Make sure it's installed and running.");
  } finally {
    clearTimeout(timeout);
  }

  if (response.status === 404) {
    throw new Error(`The embedding model "${model}" isn't installed. Pull it with "ollama pull ${model}".`);
  }
  if (!response.ok) {
    throw new Error(`Ollama responded with status ${response.status} while generating an embedding.`);
  }

  const data = (await response.json()) as OllamaEmbeddingsResponse;
  if (!data.embedding || data.embedding.length === 0) {
    throw new Error("Ollama returned no embedding vector.");
  }
  return Float32Array.from(data.embedding);
}
